import React, {
  useState,
  useEffect,
  useMemo,
  useContext,
  useCallback,
  useRef,
} from 'react';
import { unstable_batchedUpdates as batchedUpdates } from 'react-dom';
import { autorun } from 'mobx';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { observer } from 'mobx-react';
import isEqual from 'lodash.isequal';
import moment from 'moment';

import useSessionStore from '../../../hooks/useSessionStore';
import useUserDoc from '../../hooks/useUserDoc';
import useComponentMounted from '../../../hooks/useComponentMounted';
import Lead, {
  LeadState,
  SalesStage,
  MAX_TIME_THRESHOLD_TO_CLAIM_LEAD,
  ISDashboardSalesStages,
} from '../../Model/Lead';
import ExternalCoachContext from '../ExternalCoachContext';
import { ALL, DaysOptions } from '../../components/DateFilter/utils';
import { DateFormat } from '../../../utils/date';
import LeadsContext from './LeadsContext';

// Apply only on the admin "Leads" page's multi-coach view with "All" selected (see shouldPaginateAllDates in the
// load effect) — the individual coach dashboard and the IS Dashboard's tabs both fetch "All" fully unbounded.
// Pagination here works exactly like the 7/30/60-day filters already do — an unbounded fromDate/toDate window
// per coach, no per-coach doc limit — just with the window sliding back over time instead of being anchored to
// "now". The initial window is wider than the 60-day filter option so the two don't render identically on first
// load. Each Load More click extends the window further back by a fixed amount (not scaled by how far back the
// true oldest lead is) — scaling would tie the step size to coach count/data volume again, reintroducing the
// same kind of growth risk pagination was added to avoid. Most searches only need to reach a few months to
// about a year back, not all the way to the true oldest lead.
const INITIAL_ALL_LEADS_WINDOW_DAYS = 75;
const LOAD_MORE_WINDOW_MONTHS = 3;

// Whether the currently loaded window still has older leads beyond it, based on the true oldest lead date.
const hasMoreLeadsToLoad = (currentLoadedFromDate, trueOldestLeadDate) => (
  !!trueOldestLeadDate && moment(currentLoadedFromDate).isAfter(moment(trueOldestLeadDate), 'day')
);

// The earliest oldestDate across every coach's own entry, or null if none of them have one.
const getGlobalOldestDate = (oldestDatesByCoach) => Object.values(oldestDatesByCoach || {}).reduce(
  (oldest, oldestDate) => ((!oldest || (oldestDate && oldestDate < oldest)) ? oldestDate : oldest),
  null,
);

// Missing dates sort to the end regardless of comparison direction, so the comparator stays consistent
// (comparator(a, b) and comparator(b, a) must disagree, which a plain `>` on undefined never does).
const sortByDateDesc = (fieldName) => (a, b) => {
  if (!a[fieldName] && !b[fieldName]) return 0;
  if (!a[fieldName]) return 1;
  if (!b[fieldName]) return -1;
  if (a[fieldName] === b[fieldName]) return 0;
  return a[fieldName] > b[fieldName] ? -1 : 1;
};

const LeadsContextProvider = ({
  children,
}) => {
  const [leadsCollection, setLeadsCollection] = useState(null);
  const [leadsForMultipleCoaches, setLeadsForMultipleCoaches] = useState(null);
  // Older windows from Load More kept as Collections so those rows stay live-updating (status/remove changes).
  const [extraLeadsCollections, setExtraLeadsCollections] = useState([]);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [newLeadsCount, setNewLeadsCount] = useState(0);
  const [coachIdsList, setCoachIdsList] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState(DaysOptions[0]);
  const [loadedPeriod, setLoadedPeriod] = useState();
  const [loadingMoreLeads, setLoadingMoreLeads] = useState(false);
  // The true oldest lead date for the current coach selection, independent of how far pagination has loaded.
  const [oldestLeadDate, setOldestLeadDate] = useState(null);
  // Per-coach breakdown of the above (multi-coach path only), so loadMoreLeads can skip a coach once their own
  // window is exhausted instead of re-querying them forever.
  const [oldestLeadDatesByCoach, setOldestLeadDatesByCoach] = useState(null);
  // How far back the "All" date range has been loaded so far (shared across every selected coach, since every
  // coach's window is fetched to the same boundary — unlike a doc-count cursor, this needs no per-coach tracking).
  const [loadedFromDate, setLoadedFromDate] = useState(null);
  const [latestAssignedLeadsCollection, setLatestAssignedLeadsCollection] = useState({ docs: [] });
  const [showNewLeadNotificationModal, setShowNewLeadNotificationModal] = useState(false);
  const [isAgentId, setIsAgentId] = useState(null);
  const [loadedIsAgentId, setLoadedIsAgentId] = useState(null);
  const [unclaimedLeadsCollection, setUnclaimedLeadsCollection] = useState(null);
  // if true, the leads are filtered for the IS dashboard otherwise they are filtered for the leads page
  const [isISDashboardView, setIsISDashboardView] = useState(false);
  const [paymentPendingLeadsCollections, setPaymentPendingLeadsCollections] = useState(null);
  const [paymentPendingLeads, setPaymentPendingLeads] = useState([]);
  const [loadedPaymentPendingCoachIds, setLoadedPaymentPendingCoachIds] = useState(null);
  const [loadedPaymentPendingIsAgentId, setLoadedPaymentPendingIsAgentId] = useState(null);

  const isComponentMountedRef = useComponentMounted();
  const { userId: currentUserId } = useSessionStore();
  const {
    isReady: currentUserIsReady,
    userDoc: currentUserDoc,
  } = useUserDoc(currentUserId);

  const {
    externalCoachDoc,
    isReady: externalCoachReady,
    coachIds,
  } = useContext(ExternalCoachContext);

  // loadMoreLeads's closure captures selectedPeriod at the time it's called; if the user switches to a different
  // date range while a fetch is still in flight, that closure would otherwise be unaware the period it was
  // fetching for is no longer selected. A ref always reflects the latest value regardless of closure age.
  const selectedPeriodRef = useRef(selectedPeriod);
  selectedPeriodRef.current = selectedPeriod;
  const coachIdsRef = useRef(coachIds);
  coachIdsRef.current = coachIds;
  const isAgentIdRef = useRef(isAgentId);
  isAgentIdRef.current = isAgentId;

  // Load the leads collection.
  useEffect(() => {
    const isAllDatesSelected = selectedPeriod === ALL;

    const load = async () => {
      setLoadingLeads(true);
      // Load More/windowed pagination is scoped to exactly the scenario it exists for — the admin "Leads" page
      // selecting multiple/all coaches (S2-6943's "All Reps crash"). The individual coach dashboard
      // (externalCoachDoc) and the IS Dashboard's own tabs (isISDashboardView) both keep fetching "All" fully
      // unbounded, same as before this feature existed — neither is the many-coaches-combined fan-out that could
      // crash the tab.
      const shouldPaginateAllDates = isAllDatesSelected && !isISDashboardView && !externalCoachDoc;
      // "All" starts from a window wider than the 60-day filter option (so the two don't render identically on
      // first load) instead of no fromDate at all — Load More then slides this window further back. Non-"All"
      // periods are completely unchanged from before.
      let loadLeadsFrom = null;
      if (shouldPaginateAllDates) {
        loadLeadsFrom = moment().subtract(INITIAL_ALL_LEADS_WINDOW_DAYS, 'days').format(DateFormat.DEFAULT_DATE_FORMAT);
      } else if (!isAllDatesSelected) {
        loadLeadsFrom = moment().subtract(selectedPeriod, 'days').format(DateFormat.DEFAULT_DATE_FORMAT);
      }

      // React 17 doesn't auto-batch state updates outside event handlers, so each setState below the await would
      // otherwise commit as its own separate render — letting canLoadMoreLeads be computed from a new coach's
      // loadedFromDate paired with a stale oldestLeadDate (or worse, letting loadingLeads flip to false while
      // loadedPeriod is still stale, which re-triggers this same effect for a spurious extra load). Applying
      // every result together, in one batchedUpdates call, makes it all land in a single render instead.
      let applyResult;
      if (externalCoachDoc) {
        const newLeads = await externalCoachDoc.getLeads(loadLeadsFrom, isAgentId);
        applyResult = () => {
          setLeadsCollection(newLeads);
          // We need to reset the leads for the current role if we loaded leads for a single coach.
          setLeadsForMultipleCoaches(null);
          setExtraLeadsCollections([]);
          // No Load More on this path — see the comment on shouldPaginateAllDates above.
          setOldestLeadDatesByCoach(null);
          setLoadedFromDate(null);
          setOldestLeadDate(null);
        };
      } else if (coachIds?.length) {
        // Load all leads claimed by inside sales agent for the IS dashboard
        let salesStageFilters = [];
        if (isISDashboardView) {
          salesStageFilters = ISDashboardSalesStages;
        }

        const [newLeadsCollections, oldestDatesByCoach] = await Promise.all([
          Lead.getAllLeadsByCoachList(coachIds, loadLeadsFrom, isAgentId, salesStageFilters, 'submitDate'),
          shouldPaginateAllDates
            ? Lead.getOldestLeadDateByCoachList(coachIds, isAgentId, salesStageFilters, 'submitDate')
            : Promise.resolve(null),
        ]);

        applyResult = () => {
          // Reset the leads collection, which needs to be re-populated with the contents of the leads for the role.
          setLeadsCollection(null);
          setLeadsForMultipleCoaches(newLeadsCollections);
          setExtraLeadsCollections([]);
          setOldestLeadDatesByCoach(oldestDatesByCoach);
          setLoadedFromDate(shouldPaginateAllDates ? loadLeadsFrom : null);
          setOldestLeadDate(getGlobalOldestDate(oldestDatesByCoach));
        };
      } else {
        applyResult = () => {
          setLeadsCollection({});
          setLeadsForMultipleCoaches(null);
          setExtraLeadsCollections([]);
          setOldestLeadDatesByCoach(null);
          setLoadedFromDate(null);
          setOldestLeadDate(null);
        };
      }

      if (isComponentMountedRef.current) {
        batchedUpdates(() => {
          applyResult();
          setCoachIdsList(coachIds);
          setLoadedPeriod(selectedPeriod);
          setLoadedIsAgentId(isAgentId);
        });
      }
    };

    const newCoachesLoaded = !isEqual(coachIds, coachIdsList);
    const selectedPeriodChanged = selectedPeriod !== loadedPeriod;
    const isAgentIdChanged = isAgentId !== loadedIsAgentId;
    if (externalCoachReady && (newCoachesLoaded || selectedPeriodChanged || isAgentIdChanged) && !loadingLeads) {
      load();
    }
  }, [
    externalCoachReady,
    externalCoachDoc,
    leadsCollection,
    leadsForMultipleCoaches,
    loadingLeads,
    coachIds,
    coachIdsList,
    isComponentMountedRef,
    selectedPeriod,
    loadedPeriod,
    isAgentId,
    loadedIsAgentId,
    isISDashboardView,
  ]);

  // Derived so this value itself can't drift from whatever loadedFromDate/oldestLeadDate currently hold — the
  // 4 separate setCanLoadMoreLeads call sites this replaced are gone. It can still be transiently computed from
  // a mismatched pair of the two (e.g. a new coach's loadedFromDate against the previous coach's oldestLeadDate)
  // for one render during a coach switch; batchedUpdates around their setters (see the load effect) closes that.
  const canLoadMoreLeads = useMemo(
    () => selectedPeriod === ALL && hasMoreLeadsToLoad(loadedFromDate, oldestLeadDate),
    [selectedPeriod, loadedFromDate, oldestLeadDate],
  );

  // Load More only ever applies to the admin "Leads" page's multi-coach view (see shouldPaginateAllDates in the
  // load effect above) — the individual coach dashboard and the IS Dashboard's tabs both fetch "All" fully
  // unbounded with no window to page over, so canLoadMoreLeads can never be true on either of those paths, and
  // this callback has nothing to do there.
  const loadMoreLeads = useCallback(async () => {
    // coachIds/coachIdsList must still match what the currently-loaded data was fetched for — if a coach
    // selection change is still in flight (the load effect hasn't caught up yet), bail rather than merging a
    // new page into collections indexed for a different, stale coach list.
    if (
      selectedPeriod !== ALL || loadingLeads || loadingMoreLeads || !canLoadMoreLeads || !loadedFromDate
      || !isEqual(coachIds, coachIdsList)
    ) {
      return;
    }

    setLoadingMoreLeads(true);
    try {
      if (coachIds?.length) {
        // Slide the window one more month further back: the new fromDate becomes the next older boundary, and
        // the old loadedFromDate becomes the (exclusive) toDate, so we only fetch the newly-uncovered slice.
        const windowEnd = loadedFromDate;
        const windowStart = moment(loadedFromDate)
          .subtract(LOAD_MORE_WINDOW_MONTHS, 'months')
          .format(DateFormat.DEFAULT_DATE_FORMAT);
        const salesStageFilters = isISDashboardView ? ISDashboardSalesStages : [];
        // Capture the exact coach list this fetch is aligned to — nextPageCollections is index-aligned to THIS
        // array, not to whatever coachIds/coachIdsList holds once the fetch resolves.
        const requestedCoachIds = coachIds;
        const requestedIsAgentId = isAgentId;

        const nextPageCollections = await Lead.getAllLeadsByCoachList(
          requestedCoachIds, windowStart, isAgentId, salesStageFilters, 'submitDate',
          // oldestLeadDatesByCoach is already coachId -> oldestDate, ready to pass straight through — skips a
          // coach's Firestore read entirely once this window is at/before their own oldest lead.
          { toDate: windowEnd, oldestDateByCoach: oldestLeadDatesByCoach },
        );

        // Re-check after the await too — a coach selection change mid-flight, or a switch away from "All",
        // must not get merged in by index into a leadsForMultipleCoaches array rebuilt for something else.
        if (
          isComponentMountedRef.current
          && isEqual(coachIdsRef.current, requestedCoachIds)
          && isAgentIdRef.current === requestedIsAgentId
          && selectedPeriodRef.current === ALL
        ) {
          batchedUpdates(() => {
            setLoadedFromDate(windowStart);
            setExtraLeadsCollections((previousCollections) => [
              ...previousCollections,
              ...nextPageCollections,
            ]);
          });
        }
      }
    } finally {
      if (isComponentMountedRef.current) {
        setLoadingMoreLeads(false);
      }
    }
  }, [
    selectedPeriod,
    loadingLeads,
    loadingMoreLeads,
    canLoadMoreLeads,
    loadedFromDate,
    coachIdsList,
    oldestLeadDatesByCoach,
    coachIds,
    isISDashboardView,
    isAgentId,
    isComponentMountedRef,
  ]);

  const combineLeads = useCallback((leadsCollections) => {
    if (leadsCollections) {
      return leadsCollections.reduce((combinedLeadsCollection, currentCollection) => {
        const { docs } = currentCollection;

        // Build a pseudo collection with all the leads from all the collections fetched.
        return {
          docs: [
            ...combinedLeadsCollection.docs,
            ...docs,
          ],
        };
      }, { docs: [] });
    }
    return { docs: [] };
  }, []);

  useEffect(() => (
    autorun(() => {
      // We need to make the newLeads variable an observable so that we can update the tables dynamically.
      if (leadsForMultipleCoaches) {
        const { docs } = combineLeads([...leadsForMultipleCoaches, ...extraLeadsCollections]);
        // combineLeads only concatenates each sub-query's own docs array (each individually ordered by
        // Firestore, but not the combined whole) — sort once here, right after combining, rather than in the
        // downstream filter effect. Reading collection.docs here is what keeps the firestorter Collections
        // observed, including older Load More pages. The comparator
        // reads submitDate, so a submitDate change will also re-sort — removed/state toggles do not, those
        // are handled by the downstream autorun.
        const newLeads = { docs: [...docs].sort(sortByDateDesc('submitDate')) };

        // We need to update the state from within the autorun, in case the leads change after they were loaded.
        if (isComponentMountedRef.current) {
          setLeadsCollection(newLeads);
        }
      }
    })
  ), [
    leadsForMultipleCoaches,
    extraLeadsCollections,
    combineLeads,
    isComponentMountedRef,
  ]);

  // Load payment pending leads separately (no period filter) for the IS dashboard tab.
  useEffect(() => {
    const load = async () => {
      const collections = await Lead.getAllLeadsByCoachList(
        coachIds, null, isAgentId, [SalesStage.PAYMENT_PENDING],
      );

      if (isComponentMountedRef.current) {
        setPaymentPendingLeadsCollections(collections);
        setLoadedPaymentPendingCoachIds(coachIds);
        setLoadedPaymentPendingIsAgentId(isAgentId);
      }
    };

    if (!isISDashboardView || !coachIds?.length) {
      if (isComponentMountedRef.current) {
        setPaymentPendingLeadsCollections(null);
        setPaymentPendingLeads([]);
      }
      return;
    }

    const newCoachesLoaded = !isEqual(coachIds, loadedPaymentPendingCoachIds);
    const isAgentIdChanged = isAgentId !== loadedPaymentPendingIsAgentId;
    const paymentPendingCollectionsMissing = !paymentPendingLeadsCollections;
    if (isISDashboardView && externalCoachReady && (newCoachesLoaded
      || isAgentIdChanged
      || paymentPendingCollectionsMissing)) {
      load();
    }
  }, [
    isISDashboardView,
    externalCoachReady,
    coachIds,
    isAgentId,
    loadedPaymentPendingCoachIds,
    loadedPaymentPendingIsAgentId,
    paymentPendingLeadsCollections,
    isComponentMountedRef,
  ]);

  // Watch payment pending collections with autorun for MobX reactivity.
  useEffect(() => (
    autorun(() => {
      if (paymentPendingLeadsCollections) {
        const combined = combineLeads(paymentPendingLeadsCollections);
        const docs = combined.docs.filter(({ removed }) => !removed);
        if (isComponentMountedRef.current) {
          setPaymentPendingLeads(docs);
        }
      }
    })
  ), [paymentPendingLeadsCollections, combineLeads, isComponentMountedRef]);

  // This useEffect will be executed after we have loaded a leadsCollection succesfully.
  useEffect(() => {
    if (leadsCollection) {
      /*
        We need an autorun here, just in case a new lead is added to the DB while the dashboard is already loaded.
        It will also execute if any of the leads change, for example, if a row is marked as removed.
        We will also keep count of the amount of leads in the NEW state, to display it in the sidebar.
      */
      const disposer = autorun(() => {
        let newLeadsCounter = 0;
        const { docs = [] } = leadsCollection;

        // leadsCollection already arrives sorted (single-coach: Firestore-ordered directly; multi-coach: sorted
        // once when combined, see the autorun above) — filter() preserves order, so no re-sort needed here even
        // though this autorun re-runs on every doc's removed/state change, not just when new data arrives.
        const filteredDocs = docs.filter(({ removed, state }) => {
          if (state === LeadState.NEW && !removed) {
            newLeadsCounter += 1;
          }
          return !removed;
        });

        setNewLeadsCount(newLeadsCounter);
        setLeads(filteredDocs);
        setLoadingLeads(false);
      });

      return disposer;
    }
    return null;
  }, [leadsCollection]);

  // If Instant Call is enabled, it fetches the latest assigned lead for the Inside seller.
  useEffect(() => {
    const init = async () => {
      if (currentUserIsReady && currentUserDoc.instaCallIS) {
        const leadsCol = await Lead.getLatestAssignedLeads(currentUserId);

        if (isComponentMountedRef.current) {
          setLatestAssignedLeadsCollection(leadsCol);
        }
      }
    };

    init();
  }, [
    currentUserId,
    currentUserIsReady,
    currentUserDoc,
    isComponentMountedRef,
  ]);

  // Load and listen to unclaimed leads collection (only for IS users)
  useEffect(() => {
    const loadUnclaimedLeads = async () => {
      const unclaimedCollection = await Lead.getUnclaimedLeadsCollection();

      if (isComponentMountedRef.current) {
        setUnclaimedLeadsCollection(unclaimedCollection);
      }
    };

    // Only load unclaimed leads for Inside Sales users
    if (currentUserIsReady && currentUserDoc?.isInsideSales) {
      loadUnclaimedLeads();
    } else if (isComponentMountedRef.current) {
      setUnclaimedLeadsCollection(null);
    }
  }, [isComponentMountedRef, currentUserIsReady, currentUserDoc]);

  // Displays the "New Lead Notification Modal" when a new lead is assigned.
  useEffect(() => {
    // if latestAssigned lead is not claimed in the last 10 minutes, show the modal
    const isLatestLeadUnclaimed = latestAssignedLeadsCollection.docs.length > 0
      && (!latestAssignedLeadsCollection.docs[0].claimedBy
        || moment(latestAssignedLeadsCollection.docs[0].claimedAt.toDate()).isBefore(moment().subtract(10, 'minutes')));
    if (isLatestLeadUnclaimed) {
      // user can only claim the lead if it was assigned in the last 15 seconds
      const isFreshAssignedLead = moment(latestAssignedLeadsCollection.docs[0].assignedAt.toDate())
        .isSameOrAfter(moment().subtract(MAX_TIME_THRESHOLD_TO_CLAIM_LEAD, 'seconds'));
      if (isFreshAssignedLead) {
        setShowNewLeadNotificationModal(true);
      }
    }
  }, [
    latestAssignedLeadsCollection.docs,
    latestAssignedLeadsCollection.docs.length,
  ]);

  const value = useMemo(() => ({
    leadsCollection,
    leads,
    loadingLeads,
    filteredLeads,
    setFilteredLeads,
    newLeadsCount,
    setSelectedPeriod,
    selectedPeriod,
    // canLoadMoreLeads already factors in selectedPeriod === ALL (see its useMemo above).
    canLoadMoreLeads,
    loadingMoreLeads,
    loadMoreLeads,
    // The true oldest lead date, only meaningful while the "All" date range is selected.
    oldestLeadDate: selectedPeriod === ALL ? oldestLeadDate : null,
    // How far back the "All" search has actually reached — the real search boundary, not inferred from whatever
    // data happens to exist (which can lag behind if a given window has no matching leads for the active tab).
    loadedFromDate: selectedPeriod === ALL ? loadedFromDate : null,
    showNewLeadNotificationModal,
    setShowNewLeadNotificationModal,
    latestAssignedLeadDoc: latestAssignedLeadsCollection.docs.length ? latestAssignedLeadsCollection.docs[0] : {},
    setIsAgentId,
    unclaimedLeadsCollection,
    setIsISDashboardView,
    combineLeads,
    paymentPendingLeads,
  }), [
    leadsCollection,
    leads,
    loadingLeads,
    filteredLeads,
    setFilteredLeads,
    newLeadsCount,
    setSelectedPeriod,
    selectedPeriod,
    canLoadMoreLeads,
    loadingMoreLeads,
    loadMoreLeads,
    oldestLeadDate,
    loadedFromDate,
    showNewLeadNotificationModal,
    setShowNewLeadNotificationModal,
    latestAssignedLeadsCollection.docs,
    setIsAgentId,
    unclaimedLeadsCollection,
    setIsISDashboardView,
    combineLeads,
    paymentPendingLeads,
  ]);

  return (
    <LeadsContext.Provider value={value}>
      {children}
    </LeadsContext.Provider>
  );
};

LeadsContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default compose(
  observer,
)(LeadsContextProvider);
